(() => {
  'use strict';
  const clone = value => JSON.parse(JSON.stringify(value));
  const fail = code => Object.assign(new Error(code), { name: 'TCShippingError', code });
  const cents = value => {
    if (typeof value !== 'string' || !/^(0|[1-9]\d*)(\.\d{1,2})?$/.test(value)) throw fail('INVALID_RATES');
    const [whole, fraction = ''] = value.split('.');
    const result = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
    if (!Number.isSafeInteger(result)) throw fail('INVALID_RATES');
    return result;
  };
  const canonical = value => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value;
  const fingerprint = cart => JSON.stringify(canonical(cart));
  const validCart = cart => {
    if (!cart || cart.currency !== 'AUD' || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || cart.items.some(item => !item.key || !/^\d+$/.test(String(item.variant_id)) || !Number.isSafeInteger(item.quantity) || item.quantity < 1)) throw fail('INVALID_CART');
    return cart;
  };
  window.createTCShippingEstimator = (shippingConfig, commerceConfig) => {
    const config = clone(shippingConfig || {}), commerce = clone(commerceConfig || {});
    const ready = () => {
      if (config.enabled !== true || config.approvedRates !== true || commerce.enabled !== true) throw fail('DISABLED');
      if (config.version !== 1 || config.currency !== 'AUD' || commerce.currency !== 'AUD' || config.routesRoot !== commerce.routesRoot || !/^\/(?:[A-Za-z]{2}(?:-[A-Za-z0-9]{2,8})*\/)?$/.test(config.routesRoot || '')) throw fail('INVALID_CONFIG');
      if (!Array.isArray(config.countries) || !config.countries.length || config.countries.some(country => !/^[A-Z]{2}$/.test(country.code) || typeof country.name !== 'string' || !country.name || !Array.isArray(country.provinces) || typeof country.requiresProvince !== 'boolean' || typeof country.requiresPostalCode !== 'boolean' || country.provinces.some(province => !province.code || !province.name)) || new Set(config.countries.map(country => country.code)).size !== config.countries.length) throw fail('INVALID_CONFIG');
      return true;
    };
    const address = value => {
      ready();
      const country = config.countries.find(item => item.code === value?.country);
      const province = country?.provinces.find(item => item.code === value?.province);
      const zip = typeof value?.zip === 'string' ? value.zip.trim().replace(/ +/g, ' ').toUpperCase() : '';
      if (!country || (country.requiresProvince && !province) || (value.province && !province) || (country.requiresPostalCode && !zip) || zip.length > 16 || (zip && !/^[A-Z0-9 -]+$/.test(zip)) || (country.code === 'AU' && !/^\d{4}$/.test(zip))) throw fail('INVALID_ADDRESS');
      return { country: country.code, province: province?.code || '', zip, countryName: country.name, provinceName: province?.name || '' };
    };
    const request = async (path, method, signal) => {
      let response;
      try {
        response = await window.fetch(config.routesRoot + path, { method, credentials: 'same-origin', mode: 'same-origin', cache: 'no-store', redirect: 'error', headers: { Accept: 'application/json' }, signal });
        if (!response.ok) throw fail(response.status === 422 ? 'ADDRESS_REJECTED' : response.status === 429 ? 'RATE_LIMITED' : 'NETWORK');
        return await response.json();
      } catch (error) {
        if (error.name === 'TCShippingError') throw error;
        if (signal.aborted) throw fail('CANCELLED');
        throw fail('NETWORK');
      }
    };
    const estimate = async (value, expectedCart, options = {}) => {
      ready();
      const destination = address(value), expected = validCart(expectedCart);
      if (!expected.items.length) throw fail('EMPTY_CART');
      if (expected.requires_shipping === false) throw fail('NO_SHIPPING');
      if (options.signal?.aborted) throw fail('CANCELLED');
      const controller = new window.AbortController();
      let timedOut = false;
      const cancel = () => controller.abort();
      options.signal?.addEventListener('abort', cancel, { once: true });
      const timeout = window.setTimeout(() => { timedOut = true; cancel(); }, 20000);
      const check = () => { if (controller.signal.aborted) throw fail(timedOut ? 'TIMEOUT' : 'CANCELLED'); };
      try {
        check();
        const before = validCart(await request('cart.js', 'GET', controller.signal));
        if (fingerprint(before) !== fingerprint(expected)) throw fail('CART_CHANGED');
        const params = new URLSearchParams({ 'shipping_address[zip]': destination.zip, 'shipping_address[country]': destination.countryName, 'shipping_address[province]': destination.provinceName });
        await request(`cart/prepare_shipping_rates.json?${params}`, 'POST', controller.signal);
        let result = null;
        for (let attempt = 0; attempt < 8; attempt++) {
          check();
          if (attempt) await new Promise((resolve, reject) => {
            const abort = () => { window.clearTimeout(timer); reject(fail('CANCELLED')); };
            const timer = window.setTimeout(() => { controller.signal.removeEventListener('abort', abort); resolve(); }, Math.min(250 * 2 ** (attempt - 1), 1000));
            controller.signal.addEventListener('abort', abort, { once: true });
          });
          result = await request(`cart/async_shipping_rates.json?${params}`, 'GET', controller.signal);
          if (result !== null) break;
        }
        if (result === null) throw fail('TIMEOUT');
        if (!Array.isArray(result.shipping_rates)) throw fail('INVALID_RATES');
        const rates = result.shipping_rates.map(rate => {
          if (typeof rate.name !== 'string' || !rate.name.trim() || (rate.currency != null && rate.currency !== 'AUD')) throw fail('INVALID_RATES');
          return { name: rate.name, price: cents(rate.price) };
        });
        const after = validCart(await request('cart.js', 'GET', controller.signal));
        check();
        if (fingerprint(after) !== fingerprint(before)) throw fail('CART_CHANGED');
        return { destination, rates, cartFingerprint: fingerprint(after), currency: 'AUD' };
      } catch (error) { if (timedOut) throw fail('TIMEOUT'); throw error; }
      finally { window.clearTimeout(timeout); options.signal?.removeEventListener('abort', cancel); }
    };
    const progress = (cart, result) => {
      try { ready(); validCart(cart); } catch (_) { return null; }
      const offer = config.freeShipping;
      if (!result || result.cartFingerprint !== fingerprint(cart) || !result.rates.length || offer?.enabled !== true || offer.approved !== true || offer.currency !== 'AUD' || offer.subtotalBasis !== 'cart_total_after_discounts' || !Number.isSafeInteger(offer.thresholdCents) || offer.thresholdCents <= 0 || !/^\/[A-Za-z0-9/_-]+$/.test(offer.termsUrl || '') || !Array.isArray(offer.eligibleVariantIds) || !offer.eligibleVariantIds.length || !cart.items.length || !cart.items.every(item => offer.eligibleVariantIds.includes(String(item.variant_id))) || !Array.isArray(offer.destinations)) return null;
      const destination = result.destination;
      const eligible = offer.destinations.some(rule => rule.country === destination.country && (rule.allProvinces === true || Array.isArray(rule.provinces) && rule.provinces.includes(destination.province)) && (rule.allPostcodes === true || Array.isArray(rule.postcodes) && rule.postcodes.includes(destination.zip)));
      if (!eligible) return null;
      const remaining = Math.max(0, offer.thresholdCents - cart.total_price);
      return { remaining, value: Math.min(cart.total_price, offer.thresholdCents), max: offer.thresholdCents, zeroRateReturned: result.rates.some(rate => rate.price === 0), termsUrl: offer.termsUrl };
    };
    return { ready, address, estimate, progress, fingerprint };
  };

  const init = () => {
    const root = document.querySelector('[data-tc-storefront]');
    if (!root) return;
    root.tcShippingUI?.destroy();
    let config, commerce;
    try { config = JSON.parse(root.querySelector('#tc-shipping-config')?.textContent || '{}'); commerce = JSON.parse(root.querySelector('#tc-commerce-config')?.textContent || '{}'); } catch (_) { return; }
    if (config.enabled !== true || config.approvedRates !== true || commerce.enabled !== true || !root.tcCommerceUI) return;
    const estimator = window.createTCShippingEstimator(config, commerce);
    try { estimator.ready(); } catch (_) { return; }
    const t = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(String(value)), config.strings?.[key] || key);
    const money = value => `${new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value / 100)} AUD`;
    const lifecycle = new AbortController(), sections = new Map();
    const on = (el, type, fn) => el.addEventListener(type, fn, { signal: lifecycle.signal });
    const create = (tag, text, className) => { const el = document.createElement(tag); if (text !== undefined) el.textContent = text; if (className) el.className = className; return el; };
    let context = null, result = null, pending = null, sequence = 0, message = '', error = false;
    let destination = { country: '', province: '', zip: '' };
    const cancel = () => { sequence++; pending?.abort(); pending = null; };
    const invalidate = () => { cancel(); result = null; message = ''; error = false; };
    const build = (host, index) => {
      const section = create('section', undefined, 'tc-shipping');
      const heading = create('h3', t('heading')); heading.id = `tc-shipping-heading-${index}`; section.setAttribute('aria-labelledby', heading.id);
      const form = create('form');
      const fields = {};
      ['country', 'province', 'zip'].forEach(name => {
        const label = create('label', t(name)); label.htmlFor = `tc-shipping-${index}-${name}`;
        const input = create(name === 'zip' ? 'input' : 'select'); input.id = label.htmlFor; input.name = name;
        input.autocomplete = name === 'country' ? 'country' : name === 'province' ? 'address-level1' : 'postal-code';
        if (name === 'zip') { input.type = 'text'; input.maxLength = 16; }
        fields[name] = input; form.append(label, input);
      });
      const placeholder = () => { const option = create('option', t('select')); option.value = ''; return option; };
      fields.country.append(placeholder());
      config.countries.forEach(country => { const option = create('option', country.name); option.value = country.code; fields.country.append(option); });
      const button = create('button', t('check'), 'tc-outline'); button.type = 'submit'; form.append(button);
      const status = create('p', '', 'tc-small'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
      const alert = create('p', '', 'tc-commerce-error'); alert.setAttribute('role', 'alert'); alert.hidden = true;
      const list = create('ul');
      const progress = create('div'); progress.hidden = true;
      const note = create('p', t('estimate_note'), 'tc-small');
      section.append(heading, create('p', t('intro'), 'tc-small'), form, status, alert, list, progress, note);
      const updateProvince = () => {
        const country = config.countries.find(item => item.code === destination.country);
        fields.province.replaceChildren(placeholder());
        country?.provinces.forEach(province => { const option = create('option', province.name); option.value = province.code; fields.province.append(option); });
        fields.province.required = !!country?.requiresProvince;
        fields.zip.required = !!country?.requiresPostalCode;
        fields.zip.inputMode = country?.code === 'AU' ? 'numeric' : 'text';
      };
      on(form, 'input', event => {
        const name = event.target.name;
        if (!Object.hasOwn(destination, name)) return;
        destination[name] = event.target.value;
        if (name === 'country') destination.province = '';
        invalidate(); render();
      });
      on(form, 'submit', async event => {
        event.preventDefault();
        if (context?.blocked || context?.busy || context?.stale || pending || !context?.cart?.items?.length) return;
        invalidate(); const current = sequence;
        const controller = new AbortController(); pending = controller; render();
        try {
          const next = await estimator.estimate(destination, context.cart, { signal: controller.signal });
          if (current !== sequence) return;
          result = next; destination = { country: next.destination.country, province: next.destination.province, zip: next.destination.zip };
          message = t(next.rates.length ? 'ready' : 'no_rates');
        } catch (failure) {
          if (current !== sequence || failure.code === 'CANCELLED') return;
          const keys = { INVALID_ADDRESS: 'invalid_address', ADDRESS_REJECTED: 'invalid_address', CART_CHANGED: 'cart_changed', TIMEOUT: 'timeout', RATE_LIMITED: 'rate_limited', EMPTY_CART: 'empty', NO_SHIPPING: 'no_shipping' };
          message = t(keys[failure.code] || 'unavailable'); error = true;
        } finally { if (current === sequence) { pending = null; render(); } }
      });
      return { host, section, fields, button, status, alert, list, progress, updateProvince };
    };
    const render = () => {
      if (!context) return;
      const blocked = context.blocked || context.busy || context.stale || !context.cart?.items?.length;
      sections.forEach(record => {
        const { fields, section, button, status, alert, list, progress } = record;
        record.updateProvince();
        Object.entries(fields).forEach(([name, input]) => { input.value = destination[name]; input.disabled = !!blocked || !!pending || (name === 'province' && !config.countries.find(country => country.code === destination.country)?.provinces.length); });
        fields.country.required = true;
        button.disabled = !!blocked || !!pending;
        button.textContent = t(pending ? 'checking' : 'check');
        section.setAttribute('aria-busy', String(!!pending));
        status.textContent = pending ? t('checking') : !context.cart?.items?.length ? t('empty') : blocked ? t('cart_unavailable') : !error ? message : '';
        alert.textContent = error ? message : ''; alert.hidden = !error;
        list.replaceChildren(); progress.replaceChildren(); progress.hidden = true;
        if (result && !blocked) {
          result.rates.forEach(rate => list.append(create('li', `${rate.name} · ${money(rate.price)}`)));
          const offer = estimator.progress(context.cart, result);
          if (offer) {
            const label = t(offer.remaining ? 'remaining' : offer.zeroRateReturned ? 'free_estimate' : 'threshold_met', { amount: money(offer.remaining) });
            const meter = create('progress'); meter.max = offer.max; meter.value = offer.value; meter.setAttribute('aria-label', label);
            const terms = create('a', t('terms')); terms.href = offer.termsUrl;
            progress.append(create('p', label, 'tc-small'), meter, terms); progress.hidden = false;
          }
        }
      });
    };
    root.tcShippingUI = {
      captureFocus: () => document.activeElement?.closest('.tc-shipping') ? document.activeElement : null,
      sync(state, focus) {
        if (!state) return;
        const changed = context && estimator.fingerprint(context.cart) !== estimator.fingerprint(state.cart);
        if (changed || state.busy || state.stale || state.blocked) invalidate();
        context = { ...state, cart: state.cart ? clone(state.cart) : null };
        root.querySelectorAll('[data-commerce-cart]').forEach((host, index) => {
          const body = host.querySelector('[data-commerce-body]');
          if (!body) return;
          let record = sections.get(host);
          if (!record) { record = build(host, index); sections.set(host, record); }
          const totals = body.querySelector('.tc-cart-totals');
          if (record.section.parentNode !== body || record.section.nextSibling !== totals) body.insertBefore(record.section, totals);
        });
        render();
        if (focus?.isConnected && !focus.disabled) focus.focus();
      },
      destroy() { cancel(); lifecycle.abort(); sections.forEach(record => record.section.remove()); sections.clear(); if (root.tcShippingUI === this) delete root.tcShippingUI; }
    };
    root.tcShippingUI.sync(root.tcCommerceUI.getState());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', init);
})();
